import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowLeft, History, MoreHorizontal, Phone } from 'lucide-react'

import { DataTablePagination } from '@/components/common/data-table-pagination'
import { workersApi } from '@/services/workers'
import type { WorkerHotlineEntry, WorkerHotlineStatus } from '@/services/workers'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useOrganizationDateTimeFormat } from '@/lib/org-date-locale'
import { formatServerDateTimeForLocale, useTenantTimeZone } from '@/lib/tenant-date-time'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { getErrorMessage } from '@/lib/api'
import { toast } from 'sonner'

const STATUS_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'approval', label: 'Approval' },
  { value: 'denied', label: 'Denied' },
  { value: 'exited', label: 'Exited' },
]

export default function WorkerHotlineHistoryPage() {
  const serviceInstanceId = getServiceInstanceIdFromSearch(window.location.search)
  const {
    locale: organizationDateLocale,
  } = useOrganizationDateTimeFormat(serviceInstanceId)
  // Joined/exited times in the tenant timezone once it is known; until then the existing output.
  const hotlineTimeZone = useTenantTimeZone(serviceInstanceId)
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const { data, isLoading } = useQuery({
    queryKey: ['worker-hotline-history', serviceInstanceId, page, pageSize, statusFilter],
    queryFn: () =>
      workersApi.getHotlineEntries({
        service_instance_id: serviceInstanceId ?? undefined,
        page,
        page_size: pageSize,
        status: statusFilter === 'all' ? undefined : (statusFilter as WorkerHotlineStatus),
      }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 15_000,
  })

  const { data: workersData } = useQuery({
    queryKey: ['workers', serviceInstanceId, 'hotline-history-names'],
    queryFn: () => workersApi.getWorkers({ page_size: 500, service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const workerNameById = useMemo(() => {
    const map = new Map<string, string>()
    ;(workersData?.results ?? []).forEach((worker) => {
      const name =
        worker.display_name ||
        worker.full_name ||
        worker.user_registration_number ||
        `Worker ${worker.id}`
      map.set(String(worker.id), name)
    })
    return map
  }, [workersData?.results])

  const getHotlineStatusBadgeClass = (status: WorkerHotlineStatus) => {
    if (status === 'waiting') return 'border-amber-200 bg-amber-50 text-amber-800'
    if (status === 'approval') return 'border-blue-200 bg-blue-50 text-blue-700'
    if (status === 'denied') return 'border-rose-200 bg-rose-50 text-rose-700'
    return 'border-slate-200 bg-slate-100 text-slate-600'
  }

  const hotlineStatusMutation = useMutation({
    mutationFn: ({ entry, status }: { entry: WorkerHotlineEntry; status: WorkerHotlineStatus }) => {
      if (status === 'exited') {
        return workersApi.exitHotlineEntry({
          worker: entry.worker,
          service_instance_id: serviceInstanceId ?? undefined,
        })
      }

      return workersApi.updateHotlineEntry(entry.id, { status })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worker-hotline'] })
      queryClient.invalidateQueries({ queryKey: ['worker-hotline-history'] })
      queryClient.invalidateQueries({ queryKey: ['workers'] })
      toast.success('Hotline status updated')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const entries = data?.results ?? []

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Button variant="ghost" size="sm" className="-ml-2 mb-1 h-8 px-2" asChild>
            <Link to={withServiceInstanceId('/workers', serviceInstanceId)}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Back to workers
            </Link>
          </Button>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-foreground">
            <History className="h-6 w-6 text-primary" />
            Hotline History
          </h1>
          <p className="text-sm text-muted-foreground">
            All hotline queue entries, including workers who have exited.
          </p>
        </div>
      </div>

      <Card className="rounded-lg border-border/70 bg-background shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg font-medium">
                <Phone className="h-4 w-4 text-primary" />
                Queue history
              </CardTitle>
              <CardDescription>
                {data?.count ?? 0} total entr{data?.count === 1 ? 'y' : 'ies'}
              </CardDescription>
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value)
                setPage(1)
              }}
            >
              <SelectTrigger className="h-9 w-full sm:w-44">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-10 w-full rounded-md" />
              ))}
            </div>
          ) : entries.length === 0 ? (
            <p className="rounded-md border border-dashed border-border/70 px-4 py-6 text-center text-sm text-muted-foreground">
              No hotline entries found.
            </p>
          ) : (
            <>
              <div className="overflow-hidden rounded-lg border border-border/70">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Worker</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Exited</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Additional Pay</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => {
                      const statusLabel = String(entry.status_label ?? entry.status).trim() || entry.status
                      const workerName =
                        entry.worker_name ||
                        workerNameById.get(String(entry.worker)) ||
                        `Worker ${entry.worker}`
                      const isActive = entry.status !== 'exited' && !entry.exited_at

                      return (
                        <TableRow key={`hotline-history-${entry.id}`}>
                          <TableCell className="font-medium">{workerName}</TableCell>
                          <TableCell>{formatServerDateTimeForLocale(entry.joined_at, organizationDateLocale, hotlineTimeZone, '-')}</TableCell>
                          <TableCell>{formatServerDateTimeForLocale(entry.exited_at, organizationDateLocale, hotlineTimeZone, '-')}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getHotlineStatusBadgeClass(entry.status)}>
                              {statusLabel}
                            </Badge>
                          </TableCell>
                          <TableCell>{entry.additional_pay ?? '-'}</TableCell>
                          <TableCell className="max-w-[220px] truncate">{entry.note?.trim() || '-'}</TableCell>
                          <TableCell>
                            {isActive ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onSelect={(event) => {
                                      event.preventDefault()
                                      hotlineStatusMutation.mutate({ entry, status: 'waiting' })
                                    }}
                                    disabled={hotlineStatusMutation.isPending}
                                  >
                                    Mark Waiting
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={(event) => {
                                      event.preventDefault()
                                      hotlineStatusMutation.mutate({ entry, status: 'approval' })
                                    }}
                                    disabled={hotlineStatusMutation.isPending}
                                  >
                                    Mark Approval
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={(event) => {
                                      event.preventDefault()
                                      hotlineStatusMutation.mutate({ entry, status: 'denied' })
                                    }}
                                    disabled={hotlineStatusMutation.isPending}
                                  >
                                    Mark Denied
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={(event) => {
                                      event.preventDefault()
                                      hotlineStatusMutation.mutate({ entry, status: 'exited' })
                                    }}
                                    disabled={hotlineStatusMutation.isPending}
                                  >
                                    Mark Exited
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {data ? (
                <div className="mt-3 rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
                  <DataTablePagination
                    page={page}
                    pageSize={pageSize}
                    totalCount={data.count}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size)
                      setPage(1)
                    }}
                  />
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
